/**
 * Wire shapes of the purchase receipt API (batch 8 T12b1, #3033; `services/staff-api/src/routes/purchase-receipts.ts`) and the
 * props the receiving screens take from the purchase order detail.
 *
 * Quantities are integers. Nothing here carries a price except `ReceiptLineCosts`, which the server includes only for a caller
 * with `reports.view_financial`; the portal shows those values as sent and never computes a cost or a price.
 */

export const REJECT_REASONS = ['damaged', 'wrong_part', 'defective', 'short_expiry', 'documentation', 'other'] as const;
export type RejectReason = (typeof REJECT_REASONS)[number];

export const MAX_QUANTITY = 1_000_000;
export const MAX_RECEIPT_LINES = 500;
export const NOTE_MAX = 2000;
export const REFERENCE_MAX = 100;
export const CLOSE_SHORT_REASON_MAX = 500;
export const HISTORY_PAGE_SIZE = 20;
/** The server returns the latest 50 close-short events of an order, whatever the total. */
export const CLOSE_SHORT_EVENTS_BOUND = 50;

// ---------------------------------------------------------------------------
// Props: the order lines the screens work on
// ---------------------------------------------------------------------------

/**
 * One line of the purchase order as the detail view has it. `held` and `cancelled` are optional because the current detail
 * response does not carry them: without them no remaining quantity is claimed and the server alone refuses an over-receipt.
 */
export interface ReceivingLine {
  readonly id: string;
  readonly partNumber: string;
  readonly name: string;
  /** Ordered quantity. */
  readonly quantity: number;
  /** Accepted into stock so far. */
  readonly quantityReceived: number;
  readonly quantityHeld?: number | undefined;
  readonly quantityCancelled?: number | undefined;
}

// ---------------------------------------------------------------------------
// Request bodies (strict schemas of purchase-receipts.schema.ts)
// ---------------------------------------------------------------------------

export interface ReceiveLineBody {
  lineId: string;
  quantityArrived: number;
  quantityAccepted: number;
  quantityRejected: number;
  quantityHeld: number;
  rejectReason?: RejectReason;
  /** Required and never defaulted: the user states whether a quality check happened. */
  qualityObserved: boolean;
  note?: string;
}

export interface ReceiveGoodsBody {
  lines: ReceiveLineBody[];
  deliveryNoteReference?: string;
  note?: string;
}

export interface ResolveHoldBody {
  decision: 'accept' | 'reject';
  quantity: number;
  rejectReason?: RejectReason;
  note?: string;
}

export interface CloseShortBody {
  reason: string;
  lineIds?: string[];
}

// ---------------------------------------------------------------------------
// Mutation results (what the portal reads of the T09 use-case results)
// ---------------------------------------------------------------------------

export interface ReceivedLineResult {
  lineId: string;
  quantityOrdered: number;
  quantityArrived: number;
  quantityAccepted: number;
  quantityRejected: number;
  quantityHeld: number;
  qualityObserved: boolean;
  /** Current accepted-into-stock total of the order line (on a replay: the current one, not the first call's). */
  quantityReceivedTotal: number;
}

export interface ReceiveGoodsResult {
  replayed: boolean;
  receiptId: string;
  receiptNumber: string;
  purchaseOrderId: string;
  purchaseOrderStatus: string;
  fullyReceived: boolean;
  lines: ReceivedLineResult[];
}

export interface ResolveHoldResult {
  replayed: boolean;
  purchaseOrderId: string;
  receiptLineId: string;
  decision: 'accept' | 'reject';
  quantity: number;
  /** Counters of the receipt line after the decision (current ones on a replay). */
  receiptLine: { accepted: number; rejected: number; held: number };
  purchaseOrderStatus: string;
  fullyReceived: boolean;
}

export interface ClosedShortLineResult {
  lineId: string;
  cancelled: number;
  heldUnresolved: number;
  lineComplete: boolean;
}

export interface CloseShortResult {
  /** A replay shows the exact first outcome. */
  replayed: boolean;
  purchaseOrderId: string;
  purchaseOrderStatus: string;
  fullyReceived: boolean;
  reason: string;
  lines: ClosedShortLineResult[];
}

export interface OperationResultMap {
  receive: ReceiveGoodsResult;
  resolve_hold: ResolveHoldResult;
  close_short: CloseShortResult;
}
export interface OperationBodyMap {
  receive: ReceiveGoodsBody;
  resolve_hold: ResolveHoldBody;
  close_short: CloseShortBody;
}
export type OperationKind = keyof OperationResultMap;
export const OPERATION_KINDS: readonly OperationKind[] = ['receive', 'resolve_hold', 'close_short'];

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

export interface UserRef {
  id: string;
  name: string;
}

export interface HoldResolutionView {
  id: string;
  decision: 'accept' | 'reject';
  quantity: number;
  rejectReason: string | null;
  note: string | null;
  decidedBy: UserRef;
  decidedAt: string;
}

export interface ReceiptLineCosts {
  unitPriceCents: number;
  discountPercent: number;
  layers: { id: string; quantity: number; unitCostCents: string | null; costStatus: string }[];
}

export interface ReceiptLineView {
  id: string;
  purchaseOrderLineId: string;
  lineNumber: number;
  partId: string;
  partSku: string;
  partName: string;
  quantityArrived: number;
  quantityAccepted: number;
  /** Not a checked zero when `qualityObserved` is false. */
  quantityRejected: number;
  quantityHeld: number;
  quantityHeldInitial: number;
  rejectReason: string | null;
  note: string | null;
  qualityObserved: boolean;
  qualityObservedAt: string | null;
  holdResolutions: HoldResolutionView[];
  /** Only for a reader with `reports.view_financial`. */
  costs?: ReceiptLineCosts;
}

export interface ReceiptView {
  id: string;
  receiptNumber: string;
  purchaseOrderId: string;
  receivedAt: string;
  receivedBy: UserRef;
  deliveryNoteReference: string | null;
  note: string | null;
  lines: ReceiptLineView[];
}

export interface CloseShortEventView {
  id: string;
  actor: UserRef;
  reason: string;
  effectiveAt: string;
  lines: { purchaseOrderLineId: string; quantityCancelled: number }[];
}

export interface ReceiptHistoryPage {
  purchaseOrderId: string;
  items: ReceiptView[];
  pagination: { limit: number; offset: number; total: number; hasMore: boolean };
  closeShort: { total: number; items: CloseShortEventView[] };
  costsVisible: boolean;
}
